import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import Stream from 'flarum/common/utils/Stream';
import haptic from 'flarum/common/utils/haptic';

// 通知システム(独立Webアプリ)側のエンドポイント。本番移行時はサブドメインが
// 変わる可能性がある点に注意([[bbs-notification-system]]参照)。
const NOTIFY_API_BASE = 'https://notify.red4-kaigaimatome.com';

// ブラウザに「返信通知を登録済み」であることを覚えておくためのキー。
// サーバー側の保持期間(既定48h)と揃えた期限をここでも持たせ、期限切れ後は
// 再度フォームを表示する。
const NOTIFY_STORAGE_KEY = 'local-guest-post-notify';
const NOTIFY_STORAGE_TTL_MS = 48 * 60 * 60 * 1000;

/**
 * `app.session.user` が真になるダミーのユーザーもどき。プロパティに
 * 何を呼ばれても安全に無害な値(null)を返す(Proxyのcatch-all)。
 * DiscussionComposer/ReplyComposerがアバター表示等で
 * `user.displayName()` 等を呼んでもクラッシュしない。
 */
function phantomUser() {
  return new Proxy({}, {
    get(target, prop) {
      if (prop in target) return target[prop];
      if (prop === 'then' || typeof prop === 'symbol') return undefined;
      return () => null;
    }
  });
}

/**
 * コア標準の newDiscussionAction / replyAction は
 * 「app.session.user が無ければログイン画面を出す」実装になっている。
 * これらの内部では、投稿フォームの部品(DiscussionComposer等)を
 * コア自身の相対importで遅延読み込みしており、これは他の拡張機能から
 * 外部参照する形では正しく解決できない(まだ一度も読み込まれていない
 * 場合に失敗する)。そのため、ロジックを再実装するのではなく、
 * この関数呼び出しの間だけ app.session.user にダミーを差し込んで、
 * コア本来の処理をそのまま使わせる。
 *
 * `original()` 呼び出し内で `{ user: app.session.user }` が同期的に
 * 評価された直後(非同期処理が始まる前)にすぐ元へ戻すので安全。
 */
function withGuestSession(fn) {
  const previousUser = app.session.user;
  app.session.user = phantomUser();
  try {
    return fn();
  } finally {
    app.session.user = previousUser;
  }
}
function readStoredGuestNotify() {
  try {
    const raw = localStorage.getItem(NOTIFY_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || !data.email || !data.expiresAt || data.expiresAt < Date.now()) {
      localStorage.removeItem(NOTIFY_STORAGE_KEY);
      return null;
    }
    return data;
  } catch (e) {
    return null;
  }
}
function writeStoredGuestNotify(email, scope) {
  try {
    localStorage.setItem(NOTIFY_STORAGE_KEY, JSON.stringify({
      email,
      scope,
      expiresAt: Date.now() + NOTIFY_STORAGE_TTL_MS
    }));
  } catch (e) {
    // プライベートブラウジング等でlocalStorageが使えない場合は諦める
    // (次回開いたときにまた入力欄が出るだけで、機能自体に支障はない)
  }
}

/**
 * 返信通知の登録を通知システムへ依頼する。ダブルオプトイン(確認メール)の
 * ため、ここでの呼び出しはあくまで「登録リクエスト」であり、実際に通知が
 * 有効になるのは本人が確認メールのリンクをクリックした後。
 *
 * 投稿自体の成否とは独立した別リクエストのため、失敗してもエラー表示は
 * せずサイレントに諦める(投稿自体は既に成功しているか、これから成功する
 * ため、通知登録の失敗だけを理由に投稿の完了を妨げるべきではない)。
 */
function subscribeGuestNotify(email, type, discussionId) {
  fetch(`${NOTIFY_API_BASE}/api_subscribe_guest.php`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      email,
      type,
      discussion_id: discussionId || undefined
    })
  }).catch(() => {
    // 通知登録の失敗は投稿自体の成否に影響させない
  });
}

/**
 * 現在有効な(送信すべき)メールアドレスを返す。フォームに入力中の値を
 * 優先し、何も入力されていなければ、まだ有効期限内の保存済みの値を使う
 * (2回目以降、入力欄を開かなくても継続して通知登録されるようにするため)。
 */
function effectiveGuestNotifyEmail(composer) {
  if (!composer.guestNotifyEmail) return '';
  return composer.guestNotifyEmail();
}
app.initializers.add('local-guest-post', () => {
  // 「スレッドの作成」: discussion.startDiscussion 権限がゲストにあれば
  // ログイン画面を出さずにコンポーザーを開く
  override(IndexSidebar.prototype, 'newDiscussionAction', function (original) {
    if (!app.session.user && app.forum.attribute('canStartDiscussion')) {
      return withGuestSession(() => original());
    }
    return original();
  });

  // 「返信」も同様。discussion.reply 権限(discussion.canReply())があれば
  // ログイン画面を出さずにそのまま返信コンポーザーを開く
  override(DiscussionControls, 'replyAction', function (original, goToLast, forceRefresh) {
    if (!app.session.user && this.canReply()) {
      return withGuestSession(() => original(goToLast, forceRefresh));
    }
    return original(goToLast, forceRefresh);
  });

  // 以下、ComposerBody/DiscussionComposer/ReplyComposer/TextEditor を対象にした
  // 拡張は、いずれもコアの「遅延読み込みチャンク」であり、まだ一度もコンポーザーが
  // 開かれていない時点ではモジュールが存在しない。extend()/override() に
  // クラスそのものではなく文字列のモジュールパスを渡すと、Flarum core側が
  // flarum.reg.onLoad() で「実際に読み込まれたタイミング」まで自動的に
  // 適用を遅らせてくれる(公式のサポートされた使い方。実機検証で、
  // クラスを直接importして即時extendすると
  // 「TypeError: Cannot read properties of undefined (reading 'prototype')」で
  // フォーラム全体の起動が失敗することを確認した)。

  // 状態(入力中のメール・通知範囲・展開状態)は composer(ComposerState、
  // ComposerBody/TextEditor 双方から this.composer で参照できる共有オブジェクト)
  // に持たせる。TextEditorはComposerBodyとは別のコンポーネントインスタンスなので、
  // thisに直接持たせても共有できないため。
  extend('flarum/common/components/TextEditor', 'oninit', function () {
    if (app.session.user) return;
    const composer = this.attrs.composer;
    const stored = readStoredGuestNotify();
    composer.guestNotifyEmail = Stream(stored ? stored.email : '');
    composer.guestNotifyScope = Stream(stored ? stored.scope : 'replies_to_me');
    composer.guestNotifyRegistered = !!stored;
    composer.guestNotifyExpanded = false;
    composer.guestNotifyButtonRect = null;
  });

  // 投稿ボタンと同じ行(コメント入力欄の外・下側)に、控えめなベルアイコンを
  // 1つだけ表示する。クリックすると入力欄が展開する。一度登録済みの場合は
  // アイコンの見た目を変えるだけで、フォームは自動では開かない。
  extend('flarum/common/components/TextEditor', 'controlItems', function (items) {
    if (app.session.user) return;
    const composer = this.attrs.composer;
    if (!composer.guestNotifyEmail) return;
    const email = composer.guestNotifyEmail;
    const scope = composer.guestNotifyScope;

    // .TextEditor-controls (投稿ボタンの行)は overflow: auto を持っており、
    // position: absolute で浮かせるとポップアップがそこで切り取られてしまう
    // ことを実機検証で確認した。position: fixed にした上で、ボタンの画面上の
    // 位置(getBoundingClientRect)を自前で計算してstyleに直接指定することで、
    // overflow の影響を受けずに常にボタンの上に表示させる。
    const popupStyle = composer.guestNotifyButtonRect ? {
      position: 'fixed',
      left: `${composer.guestNotifyButtonRect.left}px`,
      bottom: `${window.innerHeight - composer.guestNotifyButtonRect.top + 8}px`
    } : {
      display: 'none'
    };
    items.add('guestNotify', m('span', {
      className: 'GuestPost-notifyControl'
    }, [m('button', {
      type: 'button',
      className: 'Button GuestPost-notifyButton' + (composer.guestNotifyRegistered ? ' GuestPost-notifyButton--active' : ''),
      title: composer.guestNotifyRegistered ? '返信通知:設定済み(クリックで変更)' : '返信があったらメールで通知(クリックで設定)',
      onclick: e => {
        composer.guestNotifyButtonRect = e.currentTarget.getBoundingClientRect();
        composer.guestNotifyExpanded = !composer.guestNotifyExpanded;
      }
    }, [m('i', {
      className: composer.guestNotifyRegistered ? 'fas fa-bell' : 'far fa-bell'
    }), m('span', {
      className: 'GuestPost-notifyButton-label'
    }, composer.guestNotifyRegistered ? '通知ON' : '返信通知')]), composer.guestNotifyExpanded ? m('div', {
      className: 'GuestPost-notifyPopup',
      style: popupStyle
    }, [m('div', {
      className: 'GuestPost-notifyPopup-header'
    }, [m('span', '返信通知(任意)'), m('button', {
      type: 'button',
      className: 'Button Button--icon Button--small GuestPost-notifyPopup-close',
      onclick: () => {
        composer.guestNotifyExpanded = false;
      }
    }, m('i', {
      className: 'fas fa-times'
    }))]), m('input', {
      className: 'FormControl',
      type: 'email',
      placeholder: 'メールアドレス',
      bidi: email
    }), email() ? m('div', {
      className: 'GuestPost-notify-scope'
    }, [m('label', {
      className: 'checkbox'
    }, [m('input', {
      type: 'radio',
      name: 'guestNotifyScope',
      checked: scope() === 'replies_to_me',
      onclick: () => scope('replies_to_me')
    }), '自分の投稿への返信のみ']), m('label', {
      className: 'checkbox'
    }, [m('input', {
      type: 'radio',
      name: 'guestNotifyScope',
      checked: scope() === 'discussion',
      onclick: () => scope('discussion')
    }), 'スレッド全体の新着'])]) : null]) : null]), -5);
  });

  // data()にguestNotifyEmailを乗せる(サーバー側でPostResource/DiscussionResource
  // のフィールドとして受け取り、使い捨てアカウントのusers.emailに反映される)
  extend('flarum/forum/components/ReplyComposer', 'data', function (data) {
    const email = !app.session.user ? effectiveGuestNotifyEmail(this.composer) : '';
    if (email) {
      data.guestNotifyEmail = email;
    }
  });
  extend('flarum/forum/components/DiscussionComposer', 'data', function (data) {
    const email = !app.session.user ? effectiveGuestNotifyEmail(this.composer) : '';
    if (email) {
      data.guestNotifyEmail = email;
    }
  });

  // 返信時: 返信先スレッドのIDは投稿前から分かっているので、送信と同時に
  // (投稿自体の成否を待たずに)通知登録も呼び出す。成功した(=有効なメール
  // アドレスがあった)場合はブラウザに記憶し、次回以降フォームを開かなくても
  // 継続して登録されるようにする。
  extend('flarum/forum/components/ReplyComposer', 'onsubmit', function () {
    if (app.session.user) return;
    const email = effectiveGuestNotifyEmail(this.composer);
    if (!email) return;
    const scope = this.composer.guestNotifyScope();
    subscribeGuestNotify(email, scope, this.attrs.discussion.id());
    writeStoredGuestNotify(email, scope);
  });

  // 新規スレッド作成時:「自分への返信のみ」はスレッドIDが無くても登録できる
  // ので送信と同時に呼び出せる。「スレッド全体」はスレッドIDが投稿成功後に
  // しか分からないため、コア標準のonsubmit()(vendor/flarum/core/js/src/forum/
  // components/DiscussionComposer.js)を複製し、保存成功後のコールバック内で
  // IDを添えて呼び出す(内部のPromiseを外から捕まえる手段が無いため)。
  override('flarum/forum/components/DiscussionComposer', 'onsubmit', function (original) {
    const email = !app.session.user ? effectiveGuestNotifyEmail(this.composer) : '';
    if (!email) {
      return original();
    }
    const scope = this.composer.guestNotifyScope();
    if (scope === 'replies_to_me') {
      subscribeGuestNotify(email, 'replies_to_me', null);
      writeStoredGuestNotify(email, scope);
      return original();
    }
    haptic('success');
    this.loading = true;
    const data = this.data();
    app.store.createRecord('discussions').save(data).then(discussion => {
      subscribeGuestNotify(email, 'discussion', discussion.id());
      writeStoredGuestNotify(email, scope);
      this.composer.hide();
      app.discussions.refresh();
      m.route.set(app.route.discussion(discussion));
    }, this.loaded.bind(this));
  });
});

// reCAPTCHA欄の表示順の調整はCSS側(less/forum.less)で行う。
// fof-recaptchaの拡張機能初期化タイミングがこちらより後になることがあり、
// JS側(headerItemsのpriority変更)では確実に反映できなかったため、
// 読み込み順に影響されないCSSのflex orderで対応する。